import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdmTab, AdmTabs, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup, GlassOption, GlassSelect, GlassTextInputTransformer } from "./GlassInput";
import { useContext, useEffect, useMemo, useState } from "react";
import { actionAddDataLayer, MapitStateContext } from "src/states/MapitState";
import { GlassCheckbox } from "./MitGlassComponents";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { addPreviewLayer, removePreviewLayer } from "src/utils/mapLayerHelpers";
import { GeoJSONSource } from "maplibre-gl";
import { area, bboxClip, buffer, center, centerOfMass, centroid, circle, clone, combine, concave, convex, difference, dissolve, envelope, explode, featureCollection, flatten, intersect, kinks, length, lineOffset, lineToPolygon, polygonize, polygonSmooth, polygonToLine, simplify, union, voronoi } from "@turf/turf";
import { Persistence } from "src/managers/Persistence";
import { LayerInfo } from "src/common/managers/Types";
import { Feature } from "src/states/ApplicationStateFeatures";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { unknownToFeatureCollection } from "src/functions/GeojsonHelpers";
import { planarArea, planarLength } from "src/managers/PlanarCalculationMethods";
import { AreaFunctions } from "./AreaFunctions";
import { GlassMultiLayerSelect } from "./AdvGlassInput";

export function LayerFunctions(props: { window: ManagedWindow }) {
   const { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: windowDispatch } = useWindowContext()
   const [preview, setPreview] = useState(true);
   const [selectedFunc, setSelectedFunc] = useState<string | undefined>(undefined);
   const [error, setError] = useState<string | null>(null);
   const [selectedLayer, setSelectedLayer] = useState<number[]>([]);
   const [polygonMode, setPolygonMode] = useState(true);

   const useIntl = useMemo(() => {
      return Intl.NumberFormat(Localization.getLocale(), { maximumFractionDigits: 2 })
   }, [Localization.getLocale()]);

   useEffect(() => {
      if (selectedLayer.length === 0) return;
      if (preview && selectedFunc) {
         const previewID = "layerFunPreview";
         let map = mapitState.map?.getMapPop();
         if (map.getSource(previewID)) {
            removePreviewLayer(map, previewID);
         }
         addPreviewLayer(map!, previewID, "purple", "pink");
         try {
            let features = retriveSelectedFeatures(selectedLayer, mapitState.layers);
            let funData = unknownToFeatureCollection(applyFunction(selectedFunc, featureCollection(features)));
            if (funData && funData.features && funData.features.length > 0) {
               (map.getSource(previewID) as GeoJSONSource)?.setData(funData as any);
            }
         } catch (e) {
            if (e instanceof Error) {
               console.error("Error applying function:", e.message);
               setError(e.message);
            } else {
               console.error("Unknown error applying function:", e);
               setError("An unknown error occurred while applying the function.");
            }
         }
         return () => {
            removePreviewLayer(map!, previewID);
            setError(null);
         }
      }
   }, [selectedLayer, preview, selectedFunc])

   let usableLayers = Object.values(mapitState.layers)

   return (
      <AdvancedDragModal window={props.window} title={Localization.getText("Layer Functions")}
         onClose={() => { windowDispatch(closeWindow(props.window.id)) }}
         PosDefault={{ width: "400px", height: "600px", left: "20px", top: "55px" }} >
         <AdmTabs selectedKey={polygonMode ? "polygon" : "diverse"} onChange={(key) => setPolygonMode(key === "polygon")}>
            <AdmTab eventKey="polygon" title={Localization.getText("Merge/diff areas")} />
            <AdmTab eventKey="diverse" title={Localization.getText("Complex")} />
         </AdmTabs>
         {polygonMode ? <AreaFunctions window={props.window} /> : <>
            <ADModalBody>
               <GlassInputGroup>
                  <GlassTextInputTransformer label={Localization.getText("Function")} >
                     <GlassInputControl>
                        <GlassSelect value={selectedFunc} onSelect={(e) => setSelectedFunc(e)}
                           placeholder={Localization.getText("Select function...")}
                        >
                           {Object.entries(funList).map(([key, value]) =>
                              <FunctionOption key={key} disabled={!hasAccessToFeature(value.feat)} label={value[Localization.getLanguage()]} value={key} />
                           )}
                        </GlassSelect>
                     </GlassInputControl>
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={Localization.getText("Layer")} >
                     <GlassMultiLayerSelect
                        selected={selectedLayer}
                        onSelect={(e) => setSelectedLayer(e)}
                        options={usableLayers.map((a) => ({ value: a.layerId, label: a.datasetname }))}
                     />
                  </GlassTextInputTransformer>

                  {error &&
                     <>
                        <GlassInputControlLabel>{Localization.getText("Error")}</GlassInputControlLabel>
                        <div className="LayerFunctionError">{errorToMessageLayerfunction(error, Localization.getLanguage()) ?? Localization.getText("Layerfunction:CommonError")}</div>
                     </>
                  }
                  {
                     selectedFunc &&
                     <>
                        <GlassInputControlLabel>{Localization.getText("Description")}</GlassInputControlLabel>
                        <div className="LayerfunctionDescription">
                           {Localization.getText(selectedFunc + ":desc")}
                        </div>
                     </>
                  }
                  {
                     selectedFunc && funList[selectedFunc].suffix
                        ?
                        <>
                           <GlassInputControlLabel>{Localization.getText("Result")}</GlassInputControlLabel>
                           <div className="LayerfunctionResult">
                              {useIntl.format(applyFunction(selectedFunc, featureCollection(retriveSelectedFeatures(selectedLayer, mapitState.layers))))}
                              <span className="suffix">
                                 {funList[selectedFunc].suffix}
                              </span>
                           </div>
                        </>
                        : null
                  }
               </GlassInputGroup>
            </ADModalBody>
            <ADModalFooter>
               <GlassCheckbox checked={preview} onChange={() => setPreview(!preview)} >{Localization.getText("Preview")}</GlassCheckbox>
               <button
                  disabled={!selectedFunc || funList[selectedFunc].suffix}
                  onClick={async (e) => {
                     if (!selectedFunc) return;
                     let allFeatures = retriveSelectedFeatures(selectedLayer, mapitState.layers);
                     let datafun = unknownToFeatureCollection(applyFunction(selectedFunc, featureCollection(allFeatures)));
                     if (!datafun || !datafun.features || datafun.features.length === 0) return;
                     let layerInfo = await new Promise<LayerInfo>((res) => {
                        Persistence.createLayersFromGeoJSON(
                           applyFunName(selectedFunc, mapitState.layers[selectedLayer[0]].datasetname),
                           datafun,
                           new Date(),
                           (layer) => res(layer)
                        )
                     });
                     if (e.ctrlKey) {
                        mapitDispatch(actionAddDataLayer(layerInfo));
                     } else {
                        windowDispatch(openWindow(
                           WindowId.NameOnCreation,
                           { layers: layerInfo }
                        ))
                     }
                  }} className="GlassButton">{Localization.getText("Create layer")}</button>
            </ADModalFooter>
         </>}
      </AdvancedDragModal >
   )
}

function applyFunction(func: string, geojson: any) {
   if (func == "bboxClip") {
      let bbox = [7.741269070919969, 54.47934487366999, 15.511745485770007, 57.808475243053806]
      return funList[func].func(geojson, bbox);
   }
   return funList[func].func(geojson);
}

function applyFunName(func: string, label: string) {
   let lang = Localization.getLanguage();
   return funList[func][lang] + ": " + label;
}

function retriveSelectedFeatures(selectedLayer: number[], layers: { [id: number]: LayerInfo }) {
   let features = selectedLayer.map(l => {
      let layer = layers[l];
      if (layer && layer.geoJson) {
         return layer.geoJson.features;
      }
      return [];
   }).flat();
   return features;
}

function FunctionOption({ label, value, disabled }: { label: string, value: string, disabled?: boolean }) {
   return (
      <GlassOption value={value} disabled={disabled}>
         <div className="LayerFunctionOption">
            {disabled ? <MIBIcon icon="Lock" /> : <MIBIcon icon={value as any} />}
            {label}
         </div>
      </GlassOption>
   )
}

function errorToMessageLayerfunction(error: string, language: "da" | "en"): string | null {
   let customTrans = {
      "Must have at least two features": { da: "Der skal vælges mindst to lag, vælg flere lag for at benytte funktionen", en: "At least two layers must be selected, select more layers to use this function" }
   }
   if (error in customTrans) {
      return customTrans[error][language];
   }
   return null;
}

const funList = {
   "group": { "type": "any", da: "Gruppér", en: "Group", func: (ft) => ft, feat: Feature.LayerFunctionGroup },
   "combine": { "type": "any", da: "Kombinér", en: "Combine", func: (ft) => combine(ft), feat: Feature.LayerFunctionCombine },
   "explode": { "type": "any", da: "Eksploder", en: "Explode", func: (ft) => explode(ft), feat: Feature.LayerFunctionExplode },
   "envelope": { "type": "any", da: "Konvolut", en: "Envelope", func: (ft) => envelope(ft), feat: Feature.LayerFunctionEnvelope },
   "center": { "type": "any", da: "Center", en: "Center", func: (ft) => center(ft), feat: Feature.LayerFunctionCenter },
   "centerOfMass": { "type": "any", da: "Tyngdepunkt", en: "Center of Mass", func: (ft) => centerOfMass(ft), feat: Feature.LayerFunctionCenterOfMass },
   "centroid": { "type": "any", da: "Centroid", en: "Centroid", func: (ft) => centroid(ft), feat: Feature.LayerFunctionCentroid },
   "flatten": { "type": "any", da: "Kombinér, fjern undergruppering", en: "Flatten", func: (ft) => flatten(ft), feat: Feature.LayerFunctionFlatten },

   // --- Følgende kan aktiveres når der er UI nok til at håndtere ekstra parametre ---

   "simplify": { "type": "any", da: "Forenkle", en: "Simplify", func: (ft) => simplify(ft, { tolerance: 0.01 }), feat: Feature.LayerFunctionSimplify },
   "buffer": { "type": "any", da: "Buffer", en: "Buffer", func: (ft) => buffer(ft, 1), feat: Feature.LayerFunctionBuffer },

   // --- Flere kan aktiveres når der er håndteres features i forskellige typer (point, line, polygon) ---
   "areaPrecision": { "type": "polygon", da: "Præcis Areal", en: "Precision Area", func: (ft) => planarArea(ft), suffix: "m²", feat: Feature.LayerFunctionArea },
   "areaLength": { "type": "line|polygon", da: "Præcis Længde", en: "Precision Length", func: (ft) => planarLength(ft), suffix: "m", feat: Feature.LayerFunctionLength },
   "area": { "type": "polygon", da: "Areal", en: "Area", func: (ft) => area(ft), suffix: "m²", feat: Feature.LayerFunctionArea },
   // "distance": { "type": "point_2", da: "Afstand", en: "Distance", func: (ft) => distance(ft), suffix: "km", feat: Feature.LayerFunctionDistance },
   "length": { "type": "line|polygon", da: "Længde", en: "Length", func: (ft) => length(ft, { units: "kilometers" }), suffix: "km", feat: Feature.LayerFunctionLength },
   "bboxClip": { "type": "line|polygon", da: "BBox Clip", en: "BBox Clip", func: (ft, bbox) => featureCollection(ft.features.map((a) => bboxClip(a, bbox))), feat: Feature.LayerFunctionBboxClip },
   "circle": { "type": "point", da: "Cirkel", en: "Circle", func: (ft) => circle(ft, 1), feat: Feature.LayerFunctionCircle },
   "clone": { "type": "any", da: "Klon", en: "Clone", func: (ft) => clone(ft), feat: Feature.LayerFunctionClone },
   "concave": { "type": "points", da: "Konkav", en: "Concave", func: (ft) => concave(ft), feat: Feature.LayerFunctionConcave },
   "convex": { "type": "points", da: "Konveks", en: "Convex", func: (ft) => convex(ft), feat: Feature.LayerFunctionConvex },
   "difference": { "type": "polygon", da: "Forskel", en: "Difference", func: (ft) => difference(ft), feat: Feature.LayerFunctionDifference },
   "dissolve": { "type": "polygon", da: "Opløs", en: "Dissolve", func: (ft) => dissolve(ft), feat: Feature.LayerFunctionDissolve },
   "intersect": { "type": "polygon", da: "Skæringspunkt", en: "Intersect", func: (ft) => intersect(ft), feat: Feature.LayerFunctionIntersect },
   "lineOffset": { "type": "line", da: "Linjeoffset", en: "Line Offset", func: (ft) => lineOffset(ft, 1), feat: Feature.LayerFunctionLineOffset },
   "polygonSmooth": { "type": "polygon", da: "Polygon Glat", en: "Polygon Smooth", func: (ft) => polygonSmooth(ft), feat: Feature.LayerFunctionPolygonSmooth },
   "union": { "type": "polygon", da: "Union", en: "Union", func: (ft) => union(ft), feat: Feature.LayerFunctionUnion },
   "voronoi": { "type": "points", da: "Voronoi", en: "Voronoi", func: (ft) => voronoi(ft), feat: Feature.LayerFunctionVoronoi },
   "lineToPolygon": { "type": "line", da: "Linje til Polygon", en: "Line to Polygon", func: (ft) => lineToPolygon(ft), feat: Feature.LayerFunctionLineToPolygon },
   "polygonToLine": { "type": "polygon", da: "Polygon til Linje", en: "Polygon to Line", func: (ft) => polygonToLine(ft), feat: Feature.LayerFunctionPolygonToLine },
   "polygonize": { "type": "line", da: "Polygonize", en: "Polygonize", func: (ft) => polygonize(ft), feat: Feature.LayerFunctionPolygonize },
   "kinks": { "type": "line|polygon", da: "Kinks", en: "Kinks", func: (ft) => kinks(ft), feat: Feature.LayerFunctionKinks },
}